import React, {useCallback, useMemo, useState} from "react";
import {useNavigate, useSearchParams} from "react-router-dom";

import {DataLoadingStatus, DfEntryStatus} from "shared/types/common";
import {CompareDFsResult} from "../types";
import {excelDiffService} from "../services/excelDiffService";

const useCustomDiff = () => {
    const [searchParams] = useSearchParams();
    const compare_started = useMemo(() => (searchParams.get('compare') ?? "") === "true", [searchParams]);

    const [file1Size, setFile1Size] = useState<number>(0);
    const [file1Fname, setFile1Fname] = useState<string>('');
    const [file2Size, setFile2Size] = useState<number>(0);
    const [file2Fname, setFile2Fname] = useState<string>('');

    const [dataLoadingStatus, setDataLoadingStatus] = useState<DataLoadingStatus>(DataLoadingStatus.NotLoaded);
    const [comparisonStatus, setComparisonStatus] = useState<DataLoadingStatus>(DataLoadingStatus.NotLoaded);
    const [comparisonResult, setComparisonResult] = useState<CompareDFsResult|undefined>(undefined);
    const [checkedItems, setCheckedItems] = useState({
        unmodified: true,
        modified: true,
        new: true,
        deleted: true,
    });

    const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, checked } = e.target;
        setCheckedItems({
            ...checkedItems,
            [name]: checked,
        });
    };

    const navigate = useNavigate();

    const compareData = useCallback(() => {
        setComparisonStatus(DataLoadingStatus.Loading);
        excelDiffService.compareCustom().then(resp => {
            if (resp.errorCode === undefined && resp.success && resp.result) {
                setComparisonResult(resp.result);
                setComparisonStatus(DataLoadingStatus.Loaded);
            } else {
                setComparisonResult(undefined);
                setComparisonStatus(DataLoadingStatus.Error);
            }
        });
    }, [setComparisonStatus]);

    const filteredComparisonResult = useMemo((): CompareDFsResult|undefined => {
        return comparisonResult? {columns: comparisonResult.columns, rows: comparisonResult.rows.filter(rowRes => (
                (rowRes.status === DfEntryStatus.NotChanged && checkedItems.unmodified) ||
                (rowRes.status === DfEntryStatus.Modified && checkedItems.modified) ||
                (rowRes.status === DfEntryStatus.New && checkedItems.new) ||
                (rowRes.status === DfEntryStatus.Deprecated && checkedItems.deleted)
            ))} : undefined;
    }, [comparisonResult, checkedItems]);

    const isCompareDisabled =
        dataLoadingStatus !== DataLoadingStatus.Loaded ||
        file1Size === 0 ||
        file2Size === 0 ||
        comparisonStatus === DataLoadingStatus.Loading;

    const handleCompare = () => {
        navigate(`/custom-diff?compare=true`);
        setComparisonStatus(DataLoadingStatus.NotLoaded);
    };

    if (dataLoadingStatus === DataLoadingStatus.NotLoaded) {
        setDataLoadingStatus(DataLoadingStatus.Loading);
        excelDiffService.getUploadedCustomCsvMeta().then(resp => {
            if (resp.errorCode === undefined && resp.success) {
                setFile1Size(resp.user_file1?.rows ?? 0);
                setFile1Fname(resp.user_file1?.f_name ?? '');
                setFile2Size(resp.user_file2?.rows ?? 0);
                setFile2Fname(resp.user_file2?.f_name ?? '');
                setDataLoadingStatus(DataLoadingStatus.Loaded);
            } else {
                setDataLoadingStatus(DataLoadingStatus.Error);
            }
        });
    } else if (dataLoadingStatus === DataLoadingStatus.Loaded && file1Size && file2Size && comparisonStatus === DataLoadingStatus.NotLoaded && compare_started) {
        compareData();
    }

    return {
        file1Size, setFile1Size,
        file1Fname, setFile1Fname,
        file2Size, setFile2Size,
        file2Fname, setFile2Fname,
        dataLoadingStatus,
        comparisonStatus,
        checkedItems,
        handleCheckboxChange,
        filteredComparisonResult,
        isCompareDisabled,
        handleCompare,
    };
}

export default useCustomDiff;
